import type { EditTarget } from '@web-studio/shared-schemas';
import type { ComponentPropsWithoutRef, ElementType, MouseEvent, ReactNode } from 'react';

type EditableContentProps<T extends ElementType> = {
  fieldKey: string;
  activeEditTarget?: EditTarget | null;
  editable?: boolean;
  as?: T;
  children: ReactNode;
} & ComponentPropsWithoutRef<T>;

function hasRenderableContent(children: ReactNode): boolean {
  if (children == null || children === false) {
    return false;
  }
  if (typeof children === 'string') {
    return children.trim().length > 0;
  }
  return true;
}

export function EditableContent<T extends ElementType = 'span'>({
  fieldKey,
  activeEditTarget,
  editable,
  as,
  className,
  children,
  onClick,
  ...rest
}: EditableContentProps<T>) {
  const hasContent = hasRenderableContent(children);
  // Live site: omit empty nodes. Edit mode: always mount so bound/empty PDL
  // fields keep a `data-edit-child` hit target (NBSP alone is trim()-empty).
  if (!hasContent && !editable) {
    return null;
  }

  const Tag = (as ?? 'span') as ElementType;
  const isAnchor = Tag === 'a';
  const isActive =
    Boolean(editable) &&
    activeEditTarget?.kind === 'content-field' &&
    activeEditTarget.fieldKey === fieldKey;

  const editAttrs = editable
    ? ({ 'data-edit-child': 'content-field', 'data-field-key': fieldKey } as const)
    : {};

  const elementProps =
    editable && isAnchor ? { ...(rest as object), href: '#' } : rest;

  const handleClick = (event: MouseEvent<HTMLElement>) => {
    if (editable) {
      event.preventDefault();
    }
    onClick?.(event as never);
  };

  return (
    <Tag
      className={[className, isActive ? 'ws-edit-child--active' : ''].filter(Boolean).join(' ') || undefined}
      {...editAttrs}
      {...elementProps}
      {...(editable ? { onClick: handleClick } : onClick ? { onClick } : {})}
    >
      {hasContent ? children : '\u00a0'}
    </Tag>
  );
}
